import React from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { Wallpaper, Check } from '@mui/icons-material';
import { MiniPanelProps, ExpandedPanelProps } from '../../../../types/steps';
import MiniPanel from './MiniPanel';
import ExpandedPanel from './ExpandedPanel';
import { BorderStyle } from '../../types';
import { BorderInfo } from '../../constants';
import { setBorder, setBorderState, useBorder } from '../../../../state/map';
import { clearBorder } from '../../borders';
import { useAppDispatch } from '../../../../state/types';

// Quick border options for mini panel
const quickBorderOptions = [BorderStyle.NONE, BorderStyle.SIMPLE];

export const TextMiniPanel: React.FC<MiniPanelProps> = ({ isActive, onExpand }) => {
  const currentBorder = useBorder();
  const dispatch = useAppDispatch();

  const handleBorderClick = (border: BorderStyle, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the panel
    clearBorder();
    const borderInfo = BorderInfo[border];
    dispatch(setBorder(border));
    dispatch(setBorderState(borderInfo.initialState, borderInfo.drawFn));
  };

  return (
    <MiniPanel isActive={isActive} onExpand={onExpand}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Wallpaper sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {BorderInfo[currentBorder].name} border
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {quickBorderOptions.map((border) => (
            <Chip
              key={border}
              label={BorderInfo[border].name}
              size="small"
              onClick={(e) => handleBorderClick(border, e)}
              variant={currentBorder === border ? 'filled' : 'outlined'}
              color={currentBorder === border ? 'primary' : 'default'}
              sx={{ 
                fontSize: '10px',
                height: '24px',
                cursor: 'pointer'
              }}
            />
          ))}
        </Box>
      </Box>
    </MiniPanel>
  );
};

export const TextExpandedPanel: React.FC<ExpandedPanelProps> = ({ 
  isActive, 
  onMinimize, 
  onClose 
}) => {
  const currentBorder = useBorder();
  const dispatch = useAppDispatch();

  const handleBorderClick = (border: BorderStyle) => {
    clearBorder();
    const borderInfo = BorderInfo[border];
    dispatch(setBorder(border));
    dispatch(setBorderState(borderInfo.initialState, borderInfo.drawFn));
  };

  const borderOptions = Object.values(BorderStyle).map((border) => ({
    value: border,
    label: BorderInfo[border].name,
  }));

  const BorderComponent = BorderInfo[currentBorder].input;

  return (
    <ExpandedPanel 
      isActive={isActive} 
      onMinimize={onMinimize} 
      onClose={onClose}
      title="Border & Text"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Border Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Choose a Border
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {borderOptions.map(({ value, label }) => (
              <Button
                key={value}
                variant={currentBorder === value ? 'contained' : 'outlined'}
                onClick={() => handleBorderClick(value)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                }}
                startIcon={currentBorder === value ? <Check /> : undefined}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Border Customization */}
        {currentBorder !== BorderStyle.NONE && BorderComponent && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Customize Border
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <BorderComponent />
            </Box>
          </Box>
        )}
      </Box>
    </ExpandedPanel>
  );
};
